import {
  Box,
  Button,
  LinearProgress,
  makeStyles,
  Paper,
  Typography,
  useTheme
} from '@material-ui/core'
import { ChevronRight } from '@material-ui/icons'
import { Skeleton } from '@material-ui/lab'
import clsx from 'clsx'
import React from 'react'
import { useSettlementKpiBlockStyles } from './settlementKpiBlock.styles'

const useStyles = makeStyles(useSettlementKpiBlockStyles)

interface SettlementKpiBlockProps {
  title: string
  value: number | string | undefined
  caption?: string
  /** Fase activa: el bloque ocupa más espacio y se resalta */
  isActive?: boolean
  isLoading?: boolean
  /** Si se informa, el bloque muestra el estado Procesando con este mensaje y una barra de progreso */
  processingMessage?: string
  /** Polling en pausa: no se muestra la barra de progreso */
  isProcessingPaused?: boolean
  /** Enlace bajo el valor total (ej. "Ver todas") */
  linkLabel?: string
  onLinkClick?: () => void
  isLinkSelected?: boolean
  /** Acciones al pie del bloque (botones o enlaces) */
  footer?: React.ReactNode
}

export const SettlementKpiBlock: React.FC<SettlementKpiBlockProps> = ({
  title,
  value,
  caption,
  isActive = false,
  isLoading = false,
  processingMessage,
  isProcessingPaused = false,
  linkLabel,
  onLinkClick,
  isLinkSelected = false,
  footer,
  children
}) => {
  const theme = useTheme()
  const classes = useStyles(theme)

  return (
    <Paper elevation={0} className={clsx(classes.root, isActive && classes.active)}>
      <Box className={classes.content}>
        <Typography className={clsx(classes.titleText, isActive && classes.titleTextActive)}>
          {title}
        </Typography>
        {isLoading ? (
          <Skeleton variant="rect" width="40%" height={40} style={{ marginTop: theme.spacing(1) }} />
        ) : (
          <Typography className={clsx(classes.valueText, isActive && classes.valueTextActive)}>
            {value ?? 0}
          </Typography>
        )}
        {caption && <Typography className={classes.captionText}>{caption}</Typography>}
        {processingMessage && (
          <Box className={classes.processing}>
            {!isProcessingPaused && <LinearProgress className={classes.processingBar} />}
            <Typography className={classes.processingText}>{processingMessage}</Typography>
          </Box>
        )}
        {linkLabel && onLinkClick && (
          <Button
            variant="text"
            color="primary"
            size="small"
            className={classes.linkButton}
            style={{ marginTop: theme.spacing(0.5) }}
            disabled={isLoading || !value}
            onClick={onLinkClick}
            aria-pressed={isLinkSelected}
          >
            {linkLabel}
          </Button>
        )}
        {children && <Box className={classes.rows}>{children}</Box>}
      </Box>
      {footer && <Box className={classes.footer}>{footer}</Box>}
    </Paper>
  )
}

interface SettlementKpiBlockRowProps {
  label: string
  value: number | string | undefined
  /** Color del valor */
  color?: string
  isLoading?: boolean
  /** Si se provee, la fila es clickable (consulta los resultados en la tabla) */
  onClick?: () => void
  /** Fila seleccionada: es la que está desplegada en la tabla de resultados */
  isSelected?: boolean
  /** Fila subordinada dentro de otra fila (desglose de un total) */
  isNested?: boolean
}

export const SettlementKpiBlockRow: React.FC<SettlementKpiBlockRowProps> = ({
  label,
  value,
  color,
  isLoading = false,
  onClick,
  isSelected = false,
  isNested = false,
  children
}) => {
  const theme = useTheme()
  const classes = useStyles(theme)

  const isDisabled = !isLoading && !value
  const isClickable = !!onClick && !isLoading && !isDisabled

  const handleClick = () => {
    if (isClickable) {
      onClick?.()
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (isClickable && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault()
      onClick?.()
    }
  }

  return (
    <Box
      className={clsx(
        classes.row,
        isNested && classes.rowNested,
        isSelected && classes.rowSelected,
        isDisabled && classes.rowDisabled
      )}
    >
      <Box
        className={clsx(classes.rowHeader, isClickable && classes.rowClickable)}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        tabIndex={isClickable ? 0 : undefined}
        role={isClickable ? 'button' : undefined}
        aria-pressed={isClickable ? isSelected : undefined}
      >
        <Typography className={classes.rowLabel}>{label}</Typography>
        <Box className={classes.rowValueBox}>
          {isLoading ? (
            <Skeleton variant="text" width={32} height={28} />
          ) : (
            <Typography className={classes.rowValue} style={{ color }}>
              {value ?? 0}
            </Typography>
          )}
          {onClick && <ChevronRight fontSize="small" className={classes.rowIcon} />}
        </Box>
      </Box>
      {children && <Box className={classes.nestedRows}>{children}</Box>}
    </Box>
  )
}
